import { useMemo } from 'react';
import { useQuery, UseQueryOptions } from 'react-query';
import { queryFnFetchErrorWrapper } from 'src/api/utils/errorHandlers';
import { mergeQueryOptions } from 'src/api/utils/helpers';
import { queryNames } from 'src/api/utils/queryNames';
import { useAccessToken } from 'src/hooks/useAccessToken';
import { CustomQueryOptions, FetchError } from 'src/types/TApiQueries';
import {
  getChatSessions,
  fetchChatSuggestions,
  suggestionsSearchParams,
} from '../functions/chatApi';
import { getSearch } from '../functions/assetsApi';
import type {
  ChatSessionListResponse,
  SuggestionsQuery,
  SuggestionsResponse,
} from 'src/chat/types';
import type { DashboardFilters } from 'src/types/TApiQueries';

export function useChatSessionsQuery(
  options?: CustomQueryOptions,
  queryOptions?: UseQueryOptions<ChatSessionListResponse, FetchError>,
) {
  const actualQueryOptions = mergeQueryOptions<ChatSessionListResponse>(options, queryOptions);
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => getChatSessions(accessToken)),
    [accessToken],
  );

  return useQuery<ChatSessionListResponse, FetchError>(
    [queryNames.chatSessions],
    queryFn,
    {
      ...actualQueryOptions,
      enabled: !!accessToken,
    },
  );
}

// useChatSuggestionsQuery fetches the starter chips for one surface and
// context, plus the "Browse all" manifest. Keyed by the query, so moving
// between pages/selections refetches; the catalogue itself only changes on an
// mcp-server deploy, so a 5-minute staleTime keeps chips instant on revisit.
export function useChatSuggestionsQuery(
  query: SuggestionsQuery,
  queryOptions?: UseQueryOptions<SuggestionsResponse, FetchError>,
) {
  const accessToken = useAccessToken();
  const params = suggestionsSearchParams(query);

  const queryFn = useMemo(
    () => queryFnFetchErrorWrapper(() => fetchChatSuggestions(accessToken, query)),
    // `params` is the query's identity; `query` is a new object each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accessToken, params],
  );

  return useQuery<SuggestionsResponse, FetchError>(
    [queryNames.chatSuggestions, params],
    queryFn,
    {
      keepPreviousData: true,
      ...queryOptions,
      enabled: !!accessToken && (queryOptions?.enabled ?? true),
      staleTime: 5 * 60 * 1000,
    },
  );
}

// useLibraryCountQuery returns how many assets this user can see in total —
// the chat empty state's context line ("Your library has 382 assets") and its
// new-account starters (0 = empty library). One unfiltered search with a
// single result; only the total is read. A short staleTime so the starters
// move on soon after the first upload.
export function useLibraryCountQuery(enabled: boolean) {
  const accessToken = useAccessToken();

  const queryFn = useMemo(
    () =>
      queryFnFetchErrorWrapper(() =>
        getSearch({ keyword: '', limit_per_page: 1 } as unknown as DashboardFilters, accessToken),
      ),
    [accessToken],
  );

  return useQuery<any, FetchError, number>([queryNames.libraryCount], queryFn, {
    enabled: !!accessToken && enabled,
    staleTime: 60 * 1000,
    select: (res) => Number(res?.total_files ?? 0),
  });
}
